/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import TQFont, { tqtypes, TQFontAggregation, TQFontFamily } from "../public/TQTypes";
import { TQUtils } from "../utils/TQUtils";

export class TQCustomCatalogHandler {
    private _responseBody: any = undefined;
    private _subtype: tqtypes.ApplicationFontsType;

    public constructor(responseBody: any, subtype: tqtypes.ApplicationFontsType) {
        this._responseBody = responseBody;
        this._subtype = subtype;
    }

    public parse(fontsListToUpdate: Map<string, TQFont>) {
        const aggregation = new TQFontAggregation();
        this._responseBody.forEach((font: any) => {
            const isPremium = false;
            try {
                const psName = font["postscript_name"] as string;
                if (psName == "") {
                    return;
                }

                const typekitId = font["id"] as string;
                const locFamilyNames: string[] = [];
                const locStyleNames: string[] = [];
                const lang = TQUtils.getAppropriateRegion();
                const tqFont = new TQFont(tqtypes.FontType.APPLICATION, psName);
                tqFont._subtype = this._subtype;
                let fallbackRegionFamily = "";
                let fallbackRegionStyle = "";
                const isBrowsable = font["browsable"] as boolean;

                for (const entry in font["localized_family_names"]) {
                    if (font["localized_family_names"][entry]) {
                        const name = (font["localized_family_names"][entry] as string).trim().replace(/\s+/g, " "); // Trim spaces / tabs
                        locFamilyNames.push(name + "##" + entry);
                        if (entry == lang) {
                            tqFont.familyName = name;
                        }
                        if (entry == tqtypes.Language.Default) {
                            fallbackRegionFamily = name;
                        }
                    }
                }

                for (const entry in font["localized_subfamily_names"]) {
                    if (font["localized_subfamily_names"][entry]) {
                        const name = (font["localized_subfamily_names"][entry] as string).trim().replace(/\s+/g, " "); // Trim spaces / tabs
                        locStyleNames.push(name + "##" + entry);
                        if (entry == lang) {
                            tqFont.styleName = name;
                        }
                        if (entry == tqtypes.Language.Default) {
                            fallbackRegionStyle = name;
                        }
                    }
                }

                if (tqFont.familyName == "") {
                    tqFont.familyName = fallbackRegionFamily;
                }
                if (tqFont.styleName == "") {
                    tqFont.styleName = fallbackRegionStyle;
                }

                let tqFontFamily = aggregation.getFamilyFromName(tqFont.familyName);
                if (tqFontFamily == undefined) {
                    tqFontFamily = new TQFontFamily(tqFont.familyName);
                    aggregation.addFamily(tqFont.familyName, tqFontFamily);
                }
                tqFont.fontName = tqFont.familyName + " " + tqFont.styleName;
                tqFont.fontId = typekitId;
                tqFont.isPremiumFont = isPremium;
                tqFont.accessLevel = tqtypes.AccessLevel.READ_WRITE; // Every catalog font can be read and written

                tqFont.isActiveForUser = true;
                tqFont.sortIndex = font["sort_order"] as number;
                const defLang = font["default_language"] as string;
                tqFont.script = TQUtils.getLanguageFromString(defLang);

                const supportedLanguages = new Set<tqtypes.Language>();
                tqFont.addSupportedLanguage(tqFont.script);
                for (const lang of font["language_support"]) {
                    supportedLanguages.add(TQUtils.getLanguageFromString(lang));
                }
                if (supportedLanguages.size == 0) {
                    supportedLanguages.add(tqtypes.Language.Default);
                }
                tqFont.supportedLanguages = supportedLanguages;

                if (supportedLanguages.size == 1) {
                    tqFont.script = supportedLanguages.values().next().value;
                } else {
                    TQUtils.overrideFontScriptFromPostscriptName(tqFont);
                }

                if (font["variations_postscript_name_prefix"]) {
                    tqFont.isVariableFont = true;
                    tqFont.variableFamilyPrefix = font["variations_postscript_name_prefix"] as string;
                }

                for (const entry in font["default_axes"]) {
                    tqFont.varAxisValues.set(entry, font["default_axes"][entry] as number);
                }

                if (font["instances"]) {
                    if (tqFont.variableFamilyPrefix == "") {
                        tqFont.variableFamilyPrefix = fallbackRegionFamily.trim().replace(/\W/g, ""); // Remove NonAlphaNumerics
                        tqFont.isVariableFont = true;
                    }
                    const instancesArray = font["instances"];
                    let namedInstanceSortIndex = tqFontFamily.getEntries().length;

                    // TODO; Check if we should make the current sortIndex negative
                    if (tqFontFamily.displayFont.postscriptName.toLowerCase().indexOf("italic") != -1) {
                        namedInstanceSortIndex = -instancesArray.length;
                    }
                    for (const namedInstanceEntry of instancesArray) {
                        const namedInstance = new TQFont(tqtypes.FontType.APPLICATION);
                        namedInstance._subtype = this._subtype;

                        const namedInstancePsNames = namedInstanceEntry["postscript_name"];
                        // Assign a valid postscript name
                        if (namedInstancePsNames && namedInstanceEntry["postscript_name"]) {
                            namedInstance.osCompatiblePostscriptName = namedInstanceEntry["postscript_name"];
                            namedInstance.postscriptName = namedInstanceEntry["postscript_name"];
                        }

                        for (const entry in namedInstanceEntry["localized_subfamily_names"]) {
                            if (namedInstanceEntry["localized_subfamily_names"][entry]) {
                                const name = (namedInstanceEntry["localized_subfamily_names"][entry] as string).trim().replace(/\s+/g, " "); // Trim spaces / tabs
                                if (entry == lang) {
                                    namedInstance.styleName = name;
                                    break;
                                }
                                if (entry == tqtypes.Language.Default) {
                                    fallbackRegionStyle = name;
                                }
                            }
                        }

                        if (namedInstance.styleName == "") {
                            namedInstance.styleName = fallbackRegionStyle;
                        }

                        const varPrefixPortionForPsName = tqFont.variableFamilyPrefix.trim().replace(/\W/g, ""); // Remove NonAlphaNumerics
                        const styleNamePortionForPsName = namedInstance.styleName.trim().replace(/\W/g, ""); // Remove NonAlphaNumerics
                        // Create a postscript name if still not available
                        if (styleNamePortionForPsName != "") {
                            namedInstance.postscriptName = varPrefixPortionForPsName + "-" + styleNamePortionForPsName;
                        }

                        namedInstance.fontId = tqFont.fontId;
                        namedInstance.isPremiumFont = tqFont.isPremiumFont;
                        namedInstance.accessLevel = tqtypes.AccessLevel.READ_WRITE; // Every catalog font can be read and written

                        namedInstance.sortIndex = namedInstanceSortIndex;
                        namedInstanceSortIndex = namedInstanceSortIndex + 1;
                        namedInstance.supportedLanguages = tqFont.supportedLanguages;
                        namedInstance.script = tqFont.script;
                        namedInstance.familyName = tqFont.familyName;
                        namedInstance.fontName = namedInstance.familyName + " " + namedInstance.styleName;
                        namedInstance.variableFamilyPrefix = tqFont.variableFamilyPrefix;
                        namedInstance.isVariableFont = true;
                        namedInstance.isActiveForUser = true;

                        for (const entry in namedInstanceEntry["axis_values"]) {
                            namedInstance.varAxisValues.set(entry, namedInstanceEntry["axis_values"][entry] as number);
                        }

                        tqFontFamily.displayFont = tqFont;
                        if (tqFont.variableFamilyPrefix != "") {
                            fontsListToUpdate.set(tqFont.variableFamilyPrefix, tqFont);
                            tqFontFamily.displayFont = tqFont;
                        }
                        if (namedInstance.postscriptName != "") {
                            if (isBrowsable) {
                                tqFontFamily.addFont(namedInstance.postscriptName, namedInstance);
                            } else {
                                namedInstance.isBrowsable = false;
                            }
                            fontsListToUpdate.set(namedInstance.postscriptName, namedInstance);
                            fontsListToUpdate.set(namedInstance.osCompatiblePostscriptName, namedInstance);
                            fontsListToUpdate.set(tqFont.postscriptName, tqFont);
                            fontsListToUpdate.set(tqFont.fontId, tqFont);
                        }
                    }
                } else {
                    // Regular font
                    if (isBrowsable) {
                        tqFontFamily.addFont(tqFont.postscriptName, tqFont);
                    } else {
                        tqFont.isBrowsable = false;
                    }

                    if (tqFontFamily.getRawUnorderedMap().size == 0) {
                        aggregation.removeFamily(tqFontFamily.familyName); // Remove if family isn't needed at all.
                    }
                    fontsListToUpdate.set(tqFont.postscriptName, tqFont);
                }
            } catch (e) {
                // ...
            }
        });
        return aggregation;
    }
}
